/**
 * シード値に基づく乱数生成器
 * Phaserの依存を減らすために独自実装
 */
export class RandomGenerator {
    /**
     * 乱数生成器のコンストラクタ
     * @param {number|string|Array} seed - 乱数生成のシード値
     */
    constructor(seed) {
        // シード値の処理
        this.seed = this._generateSeed(seed);
        
        // 内部状態の初期化
        this._s0 = 0;
        this._s1 = 0;
        this._s2 = 0;
        this._c = 1;
        
        // シード値を使って内部状態を初期化
        this._initState();
    }
    
    /**
     * シード値を生成する
     * @param {number|string|Array} seed - 元のシード値
     * @returns {number} - 数値化されたシード値
     * @private
     */
    _generateSeed(seed) {
        if (seed === undefined) {
            // シード値が指定されていない場合は現在時刻を使用
            return Date.now();
        }
        
        if (Array.isArray(seed)) {
            // 配列の場合は各要素を文字列化して結合し、数値に変換
            return this._hashString(seed.join(''));
        }
        
        if (typeof seed === 'string') {
            // 文字列の場合はハッシュ値を生成
            return this._hashString(seed);
        }
        
        // 数値の場合はそのまま使用
        return seed;
    }
    
    /**
     * 文字列からハッシュ値を生成する
     * @param {string} str - ハッシュ化する文字列
     * @returns {number} - ハッシュ値
     * @private
     */
    _hashString(str) {
        let hash = 0;
        
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // 32ビット整数に変換
        }
        
        return Math.abs(hash);
    }
    
    /**
     * 内部状態を初期化する
     * @private
     */
    _initState() {
        // シード値を使って内部状態を初期化
        this._s0 = this._mash(' ');
        this._s1 = this._mash(' ');
        this._s2 = this._mash(' ');
        
        this._s0 -= this._mash(this.seed.toString());
        if (this._s0 < 0) {
            this._s0 += 1;
        }
        
        this._s1 -= this._mash(this.seed.toString());
        if (this._s1 < 0) {
            this._s1 += 1;
        }
        
        this._s2 -= this._mash(this.seed.toString());
        if (this._s2 < 0) {
            this._s2 += 1;
        }
    }
    
    /**
     * 文字列から乱数を生成するヘルパー関数
     * @param {string} data - 入力文字列
     * @returns {number} - 0から1の間の乱数
     * @private
     */
    _mash(data) {
        let n = 0xefc8249d;
        
        for (let i = 0; i < data.length; i++) {
            n += data.charCodeAt(i);
            let h = 0.02519603282416938 * n;
            n = h >>> 0;
            h -= n;
            h *= n;
            n = h >>> 0;
            h -= n;
            n += h * 0x100000000; // 2^32
        }
        
        return (n >>> 0) * 2.3283064365386963e-10; // 2^-32
    }
    
    /**
     * 0から1の間の浮動小数点数を生成
     * @returns {number} - 0から1の間の乱数
     */
    frac() {
        // Alea乱数生成アルゴリズムを使用
        const t = 2091639 * this._s0 + this._c * 2.3283064365386963e-10; // 2^-32
        
        this._s0 = this._s1;
        this._s1 = this._s2;
        this._c = t | 0;
        this._s2 = t - this._c;
        
        return this._s2;
    }
    
    /**
     * 指定された範囲内の整数を生成
     * @param {number} min - 最小値（含む）
     * @param {number} max - 最大値（含む）
     * @returns {number} - 指定された範囲内の整数
     */
    between(min, max) {
        return Math.floor(this.frac() * (max - min + 1) + min);
    }
    
    /**
     * 指定された範囲内の浮動小数点数を生成
     * @param {number} min - 最小値（含む）
     * @param {number} max - 最大値（含む）
     * @returns {number} - 指定された範囲内の浮動小数点数
     */
    realBetween(min, max) {
        return this.frac() * (max - min) + min;
    }
    
    /**
     * 配列からランダムな要素を選択
     * @param {Array} array - 選択元の配列
     * @returns {*} - 配列からランダムに選択された要素
     */
    pick(array) {
        return array[Math.floor(this.frac() * array.length)];
    }
    
    /**
     * 指定された確率でtrueを返す
     * @param {number} chance - 確率（0から1の間）
     * @returns {boolean} - 確率に基づく結果
     */
    chance(chance) {
        return this.frac() < chance;
    }
    
    /**
     * 配列をランダムにシャッフル
     * @param {Array} array - シャッフルする配列
     * @returns {Array} - シャッフルされた配列
     */
    shuffle(array) {
        const result = [...array];
        
        for (let i = result.length - 1; i > 0; i--) {
            const j = Math.floor(this.frac() * (i + 1));
            [result[i], result[j]] = [result[j], result[i]];
        }
        
        return result;
    }
}
